import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {fromEvent, merge, Observable, Subscription} from "rxjs";
import {filter, takeUntil} from "rxjs/operators";
import {DatePipe, DecimalPipe} from "@angular/common";
import {CcBaseComponent} from "../../crystal-elements/cc-base.component";
import {AppConfigService} from "../app-config.service";
import {TableWsService} from "./table-ws.service";
import {TableSyncWsService} from "./table-sync-ws.service";
import {TableRequest} from "../../model/api/table-request";
import {TableRecordResult} from "../../model/api/table-record-result";
import {ApiValidation} from "../../model/api/api-validation";
import {SyncMetadata} from "../../model/api/sync-metadata";
import {DateTime} from "luxon";
import {TableColumnConfig} from "../../model/api/table-column-config";

@Component({
  template: 'nop',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
    DatePipe,
    DecimalPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableWsBaseComponent extends CcBaseComponent {

  private static nextWsViewIndex = 0;

  readonly wsViewId =
    `ws-view-${Date.now()}-${++TableWsBaseComponent.nextWsViewIndex}-${Math.random().toString(36).slice(2, 10)}`;

  wsRequestSeq = 0;
  latestWsRequestId?: string;

  syncTopic: Subscription = undefined;
  resyncTopic: Subscription = undefined;
  tableUpdateSub!: Subscription;
  forceReload: boolean = false;
  forceDatasetReloadOnce: boolean = false;

  private lastForegroundRefreshAt = 0;
  private readonly foregroundRefreshDebounceMs = 1000;

  @Input()
  tableLoading: boolean = false;
  @Output()
  tableLoadingChange = new EventEmitter<boolean>();
  tableLoadingCount: number = 0;

  tableCurrentRequest: TableRequest;
  newRequest: TableRequest;

  lastUpdated: DateTime = undefined;

  totalRecords: number = 0;

  tableConfig: TableColumnConfig[];
  records: any[] = [];

  constructor(cdref: ChangeDetectorRef,
              messageService: MessageService,
              translateService: TranslateService,
              public appConfigService: AppConfigService,
              public tableWsService: TableWsService,
              protected tableSyncWsService: TableSyncWsService<TableWsBaseComponent>) {
    super(cdref, messageService, translateService);
  }

  ngOnInit() {
    super.ngOnInit();

    this.tableWsService.getConnectionStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: boolean) => {
        if (!this.isWsRefreshEnabled()) {
          return;
        }

        if (state) {
          this.tableSyncWsService.syncRecordsRequestWs(this);

          if (this.tableCurrentRequest || this.newRequest) {
            this.setLoading();
            this.tableSyncWsService.refreshCurrentView(this);
          }
        } else {
          this.tableSyncWsService.pauseTopics(this);
        }
      });

    merge(
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible')
      ),
      fromEvent(window, 'focus')
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.handleForegroundReturn());

    this.setLoading();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.tableSyncWsService.destroyTopics(this);
  }

  private handleForegroundReturn() {
    if (!this.isWsRefreshEnabled()) {
      return;
    }
    const now = Date.now();
    if (now - this.lastForegroundRefreshAt < this.foregroundRefreshDebounceMs) {
      return;
    }
    this.lastForegroundRefreshAt = now;

    if (!this.tableCurrentRequest && !this.newRequest) {
      return;
    }

    if (!document.hasFocus() && document.visibilityState !== 'visible') {
      return;
    }

    this.setLoading();

    if (this.tableWsService.isConnected && (this.tableWsService as any).isConnected()) {
      this.tableSyncWsService.syncRecordsRequestWs(this);
      this.tableSyncWsService.refreshCurrentView(this);
    }
  }

  protected loadRecords(request: TableRequest) {
    this.newRequest = request;
    this.tableSyncWsService.tryFlushUserRequest(this);
  }

  clearReloadTable(request: TableRequest, ignoreRefresh = false) {
    if (request) {
      request.syncMetadata = undefined;
      this.tableCurrentRequest = request;
      if (!ignoreRefresh) {
        this.loadRecords(request);
        this.forceReload = true;
      }
    }
    this.markForCheck();
  }

  applyRecordResult(tableRecordResult, from: number, to: number) {
    this.records = tableRecordResult.records;
  }

  prepareRequestBeforePost(requestToPrepare: TableRequest): TableRequest {
    const request = {...requestToPrepare};
    const newFilters = [];
    if (requestToPrepare && requestToPrepare.filters && requestToPrepare.filters.length > 0) {
      requestToPrepare.filters.forEach(filter => {
        const newFilter = {...filter};
        newFilter.field = this.convertColumnIdToFieldName(newFilter.field);
        if (newFilter.field && newFilter.value && Array.isArray(newFilter.value)) {
          newFilter.value = newFilter.value.join(",");
        }
        newFilters.push(newFilter);
      });
    }
    request.filters = newFilters;

    if (request.originEvent && request.originEvent.sortField) {
      request.sortField = this.convertColumnIdToFieldName(request.originEvent.sortField);
      request.sortOrder = request.originEvent.sortOrder;
    } else {
      request.sortField = this.convertColumnIdToFieldName(request.sortField);
    }

    request.originEvent = undefined;
    return request;
  }

  public reload() {
    this.clearReloadTable(this.tableCurrentRequest);
  }

  protected convertColumnIdToFieldName(columnId: string): string {
    return columnId;
  }

  getList(request: TableRequest): Observable<TableRecordResult> {
    return undefined;
  }

  getListSync(request: TableRequest): Observable<ApiValidation<SyncMetadata>> {
    return undefined;
  }

  setLoading() {
    if (this.tableLoading) {
      return;
    }

    this.tableLoadingCount++;
    this.tableLoading = true;
    this.tableLoadingChange.emit(true);
    this.markForCheck();
  }

  setNotLoading() {
    if (!this.tableLoading && this.tableLoadingCount === 0) {
      return;
    }
    setTimeout(() => {
      this.tableLoadingCount = 0;
      this.tableLoading = false;
      this.tableLoadingChange.emit(false);
      this.markForCheck();
    });
  }

  public isWsRefreshEnabled(): boolean {
    return true;
  }
}
